import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, ButtonGroup, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPlay, faPause, faTrash, faCheck, faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import { useAuth } from '../../contexts/AuthContext';

const ListenerManager = () => {
  const [listeners, setListeners] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({
    name: '',
    type: 'https',
    config: {
      bindAddress: '0.0.0.0',
      port: 443,
      hostname: '',
      tls: {
        enabled: true,
        certPath: '',
        keyPath: ''
      }
    }
  });
  const { user } = useAuth();

  useEffect(() => {
    loadListeners();
  }, []);

  const loadListeners = async () => {
    try {
      const response = await c2Service.getListeners();
      setListeners(response.data || []);
    } catch (error) {
      console.error('Failed to load listeners:', error);
    }
  };

  const handleCreateListener = async () => {
    try {
      await c2Service.createListener(createData);
      setShowCreateModal(false);
      setCreateData({
        name: '',
        type: 'https',
        config: {
          bindAddress: '0.0.0.0',
          port: 443,
          hostname: '',
          tls: { enabled: true, certPath: '', keyPath: '' }
        }
      });
      loadListeners();
    } catch (error) {
      console.error('Failed to create listener:', error);
      alert('Failed to create listener: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleStartListener = async (listenerId) => {
    try {
      await c2Service.startListener(listenerId);
      loadListeners();
    } catch (error) {
      console.error('Failed to start listener:', error);
      alert('Failed to start listener: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleStopListener = async (listenerId) => {
    try {
      await c2Service.stopListener(listenerId);
      loadListeners();
    } catch (error) {
      console.error('Failed to stop listener:', error);
    }
  };

  const handleDeleteListener = async (listenerId) => {
    if (!window.confirm('Delete this listener? This will disconnect all beacons using it.')) {
      return;
    }

    try {
      await c2Service.deleteListener(listenerId);
      loadListeners();
    } catch (error) {
      console.error('Failed to delete listener:', error);
    }
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      http: { bg: 'warning', label: 'HTTP' },
      https: { bg: 'success', label: 'HTTPS' },
      dns: { bg: 'info', label: 'DNS' },
      smb: { bg: 'primary', label: 'SMB' },
      tcp: { bg: 'secondary', label: 'TCP' },
      external_c2: { bg: 'dark', label: 'External C2' }
    };
    const config = typeConfig[type] || { bg: 'secondary', label: type };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const canCreate = user?.permissions?.includes('c2.listeners.create');
  const canStart = user?.permissions?.includes('c2.listeners.start');
  const canStop = user?.permissions?.includes('c2.listeners.stop');
  const canDelete = user?.permissions?.includes('c2.listeners.delete');

  return (
    <>
      <Card>
        <Card.Header style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
          <strong style={{ color: '#fff' }}>Listeners ({listeners?.length || 0})</strong>
          <div>
            {canCreate && (
              <button
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                onClick={() => setShowCreateModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} /> Create Listener
              </button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#374151', borderBottom: '1px solid #4b5563' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Name</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Type</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Bind Address</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Port</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Hostname</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>TLS</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Statistics</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#9ca3af' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(listeners) ? listeners : []).map(listener => (
                <tr key={listener._id} style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {listener.isActive ? (
                      <Badge bg="success">● Active</Badge>
                    ) : (
                      <Badge bg="secondary">○ Stopped</Badge>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <strong>{listener.name}</strong>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {getTypeBadge(listener.type)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <code>{listener.config?.bindAddress || '0.0.0.0'}</code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <code>{listener.config?.port}</code>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small>
                      {listener.config?.hostname || 'N/A'}
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    {listener.config?.tls?.enabled ? (
                      <Badge bg="success"><FontAwesomeIcon icon={faCheck} /> Enabled</Badge>
                    ) : (
                      <Badge bg="secondary"><FontAwesomeIcon icon={faTimes} /> Disabled</Badge>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#9ca3af' }}>
                    <small>
                      {listener.stats?.activeBeacons || 0} beacons<br />
                      {listener.stats?.totalConnections || 0} connections
                    </small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#e5e7eb' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem' }}>
                      {!listener.isActive && canStart && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => handleStartListener(listener._id)}
                        >
                          <FontAwesomeIcon icon={faPlay} /> Start
                        </button>
                      )}
                      {listener.isActive && canStop && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                          onClick={() => handleStopListener(listener._id)}
                        >
                          <FontAwesomeIcon icon={faPause} /> Stop
                        </button>
                      )}
                      {canDelete && (
                        <button
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: listener.isActive ? '#6b7280' : '#dc2626', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: listener.isActive ? 'not-allowed' : 'pointer', opacity: listener.isActive ? 0.5 : 1 }}
                          onClick={() => handleDeleteListener(listener._id)}
                          disabled={listener.isActive}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {listeners.length === 0 && (
            <div className="text-center text-muted py-5">
              <p className="mb-0">No listeners configured</p>
              <small>Create a listener to accept beacon connections</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create Listener Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Listener</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Production HTTPS Listener"
                value={createData.name}
                onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={createData.type}
                onChange={(e) => {
                  const type = e.target.value;
                  const defaultPorts = {
                    http: 80,
                    https: 443,
                    dns: 53,
                    smb: 445,
                    tcp: 8080
                  };
                  setCreateData({
                    ...createData,
                    type: type,
                    config: {
                      ...createData.config,
                      port: defaultPorts[type] || 443
                    }
                  });
                }}
              >
                <option value="http">HTTP (Not Recommended)</option>
                <option value="https">HTTPS (Recommended)</option>
                <option value="dns">DNS (Advanced)</option>
                <option value="smb">SMB (Advanced)</option>
                <option value="tcp">TCP (Custom)</option>
              </Form.Select>
              {createData.type === 'http' && (
                <Alert variant="warning" className="mt-2 mb-0">
                  <small>
                    <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Warning:</strong> HTTP traffic is not encrypted. Use HTTPS for production.
                  </small>
                </Alert>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bind Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="0.0.0.0"
                    value={createData.config.bindAddress}
                    onChange={(e) => setCreateData({
                      ...createData,
                      config: { ...createData.config, bindAddress: e.target.value }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Use 0.0.0.0 to listen on all interfaces
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Port</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="65535"
                    value={createData.config.port}
                    onChange={(e) => setCreateData({
                      ...createData,
                      config: { ...createData.config, port: parseInt(e.target.value) }
                    })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Hostname (Public URL)</Form.Label>
              <Form.Control
                type="text"
                placeholder="c2.example.com"
                value={createData.config.hostname}
                onChange={(e) => setCreateData({
                  ...createData,
                  config: { ...createData.config, hostname: e.target.value }
                })}
              />
              <Form.Text className="text-muted">
                The public hostname or IP that beacons will connect to
              </Form.Text>
            </Form.Group>

            {(createData.type === 'https' || createData.type === 'http') && (
              <>
                <hr />
                <h6>TLS Configuration</h6>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable TLS/SSL"
                    checked={createData.config.tls.enabled}
                    onChange={(e) => setCreateData({
                      ...createData,
                      config: {
                        ...createData.config,
                        tls: { ...createData.config.tls, enabled: e.target.checked }
                      }
                    })}
                  />
                </Form.Group>

                {createData.config.tls.enabled && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Certificate Path</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="/etc/letsencrypt/live/c2.example.com/fullchain.pem"
                        value={createData.config.tls.certPath}
                        onChange={(e) => setCreateData({
                          ...createData,
                          config: {
                            ...createData.config,
                            tls: { ...createData.config.tls, certPath: e.target.value }
                          }
                        })}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Private Key Path</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="/etc/letsencrypt/live/c2.example.com/privkey.pem"
                        value={createData.config.tls.keyPath}
                        onChange={(e) => setCreateData({
                          ...createData,
                          config: {
                            ...createData.config,
                            tls: { ...createData.config.tls, keyPath: e.target.value }
                          }
                        })}
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}

            {createData.type === 'dns' && (
              <>
                <hr />
                <h6>DNS Configuration</h6>
                <Form.Group className="mb-3">
                  <Form.Label>DNS Zone</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="c2.example.com"
                    value={createData.config.dnsZone || ''}
                    onChange={(e) => setCreateData({
                      ...createData,
                      config: { ...createData.config, dnsZone: e.target.value }
                    })}
                  />
                  <Form.Text className="text-muted">
                    DNS zone for C2 communication (requires NS record delegation)
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {createData.type === 'smb' && (
              <>
                <hr />
                <h6>SMB Configuration</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Pipe Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="\\.\pipe\msagent_12"
                    value={createData.config.pipeName || ''}
                    onChange={(e) => setCreateData({
                      ...createData,
                      config: { ...createData.config, pipeName: e.target.value }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Named pipe for SMB peer-to-peer communication
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateListener}>
            <FontAwesomeIcon icon={faPlus} /> Create Listener
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ListenerManager;
